import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminTopNavbar from './components/AdminTopNavbar';
import { getAdminSettings, updateAdminSettings, sendTestEmail, uploadFavicon } from '../api/adminApi';
import '../styles/admin-unified.css';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    general: {
      siteName: '',
      siteDescription: '',
      siteLogo: '',
      favicon: '',
    },
    security: {
      passwordPolicy: 'medium',
      twoFactorAuth: false
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: false
    },
    appearance: {
      theme: 'light',
      primaryColor: '#4F46E5',
      secondaryColor: '#10B981'
    },
    payment: {
      paymentGateway: 'razorpay',
      stripePublicKey: '',
      stripeSecretKey: '',
      paypalClientId: '',
      paypalSecret: '',
      razorpay: {
        mode: 'test',
        testKeyId: '',
        testKeySecret: '',
        liveKeyId: '',
        liveKeySecret: ''
      },
      currency: 'INR',
      taxRate: 0,
      invoicePrefix: 'INV-'
    },
    smtp: {
      host: '',
      port: 587,
      username: '',
      password: '',
      encryption: 'tls',
      fromName: '',
      fromEmail: '',
      replyToEmail: ''
    }
  });
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconLoading, setFaviconLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getAdminSettings();
      
      // Ensure all required properties exist with default values
      const defaultSettings = {
        general: {
          siteName: '',
          siteDescription: '',
          siteLogo: '',
          favicon: '',
        },
        payment: {
          paymentGateway: 'stripe',
          stripePublicKey: '',
          stripeSecretKey: '',
          paypalClientId: '',
          paypalSecret: '',
          razorpayKeyId: '',
          razorpayKeySecret: '',
          currency: 'USD',
          taxRate: 0,
          invoicePrefix: 'INV-',
        },
        smtp: {
          host: '',
          port: 587,
          username: '',
          password: '',
          encryption: 'tls',
          fromName: '',
          fromEmail: '',
          replyToEmail: '',
        },
      };
      
      // Merge the API data with default settings to ensure all properties exist
      const mergedSettings = {
        general: { ...defaultSettings.general, ...(data.general || {}) },
        payment: { ...defaultSettings.payment, ...(data.payment || {}) },
        smtp: { ...defaultSettings.smtp, ...(data.smtp || {}) },
      };
      
      setSettings(mergedSettings);
    } catch (err) {
      setError(err.message || 'Failed to fetch settings');
      toast.error(err.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleRazorpayModeChange = (mode) => {
    setSettings(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        razorpay: {
          ...prev.payment.razorpay,
          mode: mode
        }
      }
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleInputChange('general', 'siteLogo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFaviconFile(file);
      toast.success('Favicon selected successfully');
    }
  };

  const handleFaviconUpload = async () => {
    if (!faviconFile) {
      toast.error('Please select a favicon file first');
      return;
    }

    try {
      setFaviconLoading(true);
      const response = await uploadFavicon(faviconFile);
      
      if (response) {
        const newFaviconPath = response.data?.url || response.data?.path || '/uploads/favicons/favicon.ico';
        setSettings(prev => ({
          ...prev,
          general: {
            ...prev.general,
            favicon: newFaviconPath
          }
        }));
        setFaviconFile(null);
        toast.success('Favicon uploaded successfully');
        await fetchSettings();
      } else {
        throw new Error('No response received from server');
      }
    } catch (error) {
      console.error('Error uploading favicon:', error);
      toast.error('Failed to upload favicon');
    } finally {
      setFaviconLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      
      // Create a copy of settings to modify, excluding the favicon if it's a File object
      const settingsToSave = {
        ...settings,
        general: {
          ...settings.general,
          favicon: settings.general.favicon instanceof File ? '' : settings.general.favicon
        }
      };

      // Update settings
      const updateResponse = await updateAdminSettings(settingsToSave);
      
      // Consider any response as successful if it exists
      if (updateResponse) {
      toast.success('Settings updated successfully');
        // Refresh settings after successful update
        await fetchSettings();
      } else {
        throw new Error('No response received from server');
      }
    } catch (err) {
      console.error('Error in handleSaveSettings:', err);
      setError(err.message || 'Failed to update settings');
      toast.error(err.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setLoading(true);
      const response = await sendTestEmail();
      toast.success('Test email sent successfully');
    } catch (err) {
      setError(err.message || 'Failed to send test email');
      toast.error(err.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const getFaviconUrl = (faviconPath) => {
    if (!faviconPath) return '';
    
    // If it's a File object, create a temporary URL
    if (faviconPath instanceof File) {
      return URL.createObjectURL(faviconPath);
    }
    
    // Convert to string if it's not already
    const pathString = String(faviconPath);
    
    // If it's already a full URL, return it
    if (pathString.startsWith('http')) {
      return pathString;
    }
    
    // For relative paths, use the standard favicon path
    return `${process.env.REACT_APP_URL}/uploads/favicons/favicon.ico`;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            {/* General Settings Section */}
            <div className="admin-card">
              <h3 className="text-lg font-medium text-accent mb-4">General Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="admin-label">Site Name</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.general.siteName}
                  onChange={(e) => handleInputChange('general', 'siteName', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">Site Description</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.general.siteDescription}
                  onChange={(e) => handleInputChange('general', 'siteDescription', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="admin-label">Site Logo</label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Enter logo URL"
                      className="admin-input"
                      value={settings.general.siteLogo}
                      onChange={(e) => handleInputChange('general', 'siteLogo', e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="logo-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                    />
                    <label
                      htmlFor="logo-upload"
                      className="admin-btn admin-btn-primary cursor-pointer"
                    >
                      Upload
                    </label>
                  </div>
                </div>
                {settings.general.siteLogo && (
                  <div className="mt-2">
                    <img 
                      src={settings.general.siteLogo} 
                      alt="Site Logo Preview" 
                      className="h-12 object-contain"
                    />
                  </div>
                )}
              </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  onClick={handleSaveSettings}
                >
                  Save General Settings
                </button>
              </div>
            </div>

            {/* Favicon Section */}
            <div className="admin-card">
              <h3 className="text-lg font-medium text-accent mb-4">Favicon Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Current favicon URL"
                      className="admin-input"
                      value={settings.general.favicon || ''}
                      readOnly
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="favicon-upload"
                      className="hidden"
                      accept=".ico"
                      onChange={handleFaviconChange}
                    />
                    <label
                      htmlFor="favicon-upload"
                      className="admin-btn admin-btn-secondary cursor-pointer"
                    >
                      Select Favicon
                    </label>
                  </div>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={handleFaviconUpload}
                    disabled={!faviconFile || faviconLoading}
                  >
                    {faviconLoading ? 'Uploading...' : 'Upload Favicon'}
                  </button>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-muted mb-2">Favicon Preview:</p>
                  <div className="flex items-center space-x-4">
                {settings.general.favicon && (
                      <div>
                        <p className="text-sm text-muted mb-1">Current:</p>
                    <img 
                          src={getFaviconUrl(settings.general.favicon)} 
                          alt="Current Favicon" 
                          className="w-8 h-8"
                        />
                      </div>
                    )}
                    {faviconFile && (
                      <div>
                        <p className="text-sm text-muted mb-1">New:</p>
                        <img 
                          src={URL.createObjectURL(faviconFile)} 
                          alt="New Favicon Preview" 
                      className="w-8 h-8"
                    />
                  </div>
                )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'payment':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="admin-label">Payment Gateway</label>
                <select
                  className="admin-select"
                  value={settings.payment.paymentGateway}
                  onChange={(e) => handleInputChange('payment', 'paymentGateway', e.target.value)}
                >
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              <div>
                <label className="admin-label">Currency</label>
                <select
                  className="admin-select"
                  value={settings.payment.currency}
                  onChange={(e) => handleInputChange('payment', 'currency', e.target.value)}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </select>
              </div>
              <div>
                <label className="admin-label">Tax Rate (%)</label>
                <input
                  type="number"
                  className="admin-input"
                  value={settings.payment.taxRate}
                  onChange={(e) => handleInputChange('payment', 'taxRate', parseFloat(e.target.value))}
                />
              </div>
              <div>
                <label className="admin-label">Invoice Prefix</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.payment.invoicePrefix}
                  onChange={(e) => handleInputChange('payment', 'invoicePrefix', e.target.value)}
                />
              </div>
              {settings.payment.paymentGateway === 'stripe' && (
                <>
                  <div>
                    <label className="admin-label">Stripe Public Key</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={settings.payment.stripePublicKey}
                      onChange={(e) => handleInputChange('payment', 'stripePublicKey', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="admin-label">Stripe Secret Key</label>
                    <input
                      type="password"
                      className="admin-input"
                      value={settings.payment.stripeSecretKey}
                      onChange={(e) => handleInputChange('payment', 'stripeSecretKey', e.target.value)}
                    />
                  </div>
                </>
              )}
              {settings.payment.paymentGateway === 'paypal' && (
                <>
                  <div>
                    <label className="admin-label">PayPal Client ID</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={settings.payment.paypalClientId}
                      onChange={(e) => handleInputChange('payment', 'paypalClientId', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="admin-label">PayPal Secret</label>
                    <input
                      type="password"
                      className="admin-input"
                      value={settings.payment.paypalSecret}
                      onChange={(e) => handleInputChange('payment', 'paypalSecret', e.target.value)}
                    />
                  </div>
                </>
              )}
              {settings.payment.paymentGateway === 'razorpay' && (
                <>
                  <div className="mb-4">
                    <label className="admin-label">Mode</label>
                    <div className="admin-tabs">
                      <button
                        type="button"
                        className={`admin-tab ${
                          settings.payment.razorpay.mode === 'test' ? 'active' : ''
                        }`}
                        onClick={() => handleRazorpayModeChange('test')}
                      >
                        Test Mode
                      </button>
                      <button
                        type="button"
                        className={`admin-tab ${
                          settings.payment.razorpay.mode === 'live' ? 'active' : ''
                        }`}
                        onClick={() => handleRazorpayModeChange('live')}
                      >
                        Live Mode
                      </button>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="admin-label">
                      {settings.payment.razorpay.mode === 'test' ? 'Test Key ID' : 'Live Key ID'}
                    </label>
                    <input
                      type="text"
                      className="admin-input"
                      value={settings.payment.razorpay.mode === 'test' 
                        ? settings.payment.razorpay.testKeyId 
                        : settings.payment.razorpay.liveKeyId}
                      onChange={(e) => {
                        const field = settings.payment.razorpay.mode === 'test' ? 'testKeyId' : 'liveKeyId';
                        setSettings(prev => ({
                          ...prev,
                          payment: {
                            ...prev.payment,
                            razorpay: {
                              ...prev.payment.razorpay,
                              [field]: e.target.value
                            }
                          }
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="admin-label">
                      {settings.payment.razorpay.mode === 'test' ? 'Test Key Secret' : 'Live Key Secret'}
                    </label>
                    <input
                      type="password"
                      className="admin-input"
                      value={settings.payment.razorpay.mode === 'test' 
                        ? settings.payment.razorpay.testKeySecret 
                        : settings.payment.razorpay.liveKeySecret}
                      onChange={(e) => {
                        const field = settings.payment.razorpay.mode === 'test' ? 'testKeySecret' : 'liveKeySecret';
                        setSettings(prev => ({
                          ...prev,
                          payment: {
                            ...prev.payment,
                            razorpay: {
                              ...prev.payment.razorpay,
                              [field]: e.target.value
                            }
                          }
                        }));
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      case 'smtp':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="admin-label">SMTP Host</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.smtp.host}
                  onChange={(e) => handleInputChange('smtp', 'host', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">SMTP Port</label>
                <input
                  type="number"
                  className="admin-input"
                  value={settings.smtp.port}
                  onChange={(e) => handleInputChange('smtp', 'port', parseInt(e.target.value))}
                />
              </div>
              <div>
                <label className="admin-label">SMTP Username</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.smtp.username}
                  onChange={(e) => handleInputChange('smtp', 'username', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">SMTP Password</label>
                <input
                  type="password"
                  className="admin-input"
                  value={settings.smtp.password}
                  onChange={(e) => handleInputChange('smtp', 'password', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">Encryption</label>
                <select
                  className="admin-select"
                  value={settings.smtp.encryption}
                  onChange={(e) => handleInputChange('smtp', 'encryption', e.target.value)}
                >
                  <option value="tls">TLS</option>
                  <option value="ssl">SSL</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label className="admin-label">From Name</label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.smtp.fromName}
                  onChange={(e) => handleInputChange('smtp', 'fromName', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">From Email</label>
                <input
                  type="email"
                  className="admin-input"
                  value={settings.smtp.fromEmail}
                  onChange={(e) => handleInputChange('smtp', 'fromEmail', e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">Reply-To Email</label>
                <input
                  type="email"
                  className="admin-input"
                  value={settings.smtp.replyToEmail}
                  onChange={(e) => handleInputChange('smtp', 'replyToEmail', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  onClick={handleSendTestEmail}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <AdminTopNavbar />
        <div className="admin-loading">
          <div className="admin-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <AdminTopNavbar />
        <div className="admin-content">
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg relative backdrop-blur-sm" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminTopNavbar />

      <div className="admin-content pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="admin-card mb-6">
            <div className="admin-tabs">
              <nav className="flex -mb-px overflow-x-auto">
                <button
                  className={`admin-tab ${activeTab === 'general' ? 'active' : ''}`}
                  onClick={() => setActiveTab('general')}
                >
                  ⚙️ General
                </button>
                <button
                  className={`admin-tab ${activeTab === 'payment' ? 'active' : ''}`}
                  onClick={() => setActiveTab('payment')}
                >
                  💳 Payment
                </button>
                <button
                  className={`admin-tab ${activeTab === 'smtp' ? 'active' : ''}`}
                  onClick={() => setActiveTab('smtp')}
                >
                  📧 SMTP
                </button>
              </nav>
            </div>
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;